import { Backdrop, Dialog, DialogContent, Paper, Stack, Typography } from '@mui/material';
import type { BackdropProps, PaperProps, TypographyProps } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { motion } from 'motion/react';
import type { MotionProps } from 'motion/react';
import { cloneElement, forwardRef, isValidElement, useEffect } from 'react';
import type { ComponentType, ReactNode, Ref } from 'react';

import { CardIcon } from './CardIcon';

/**
 * A card's edit mode, as a modal the card grows into (decision 16) — ported
 * from Web's `ExpandableCardDialog` (`Web/src/components/ui/ExpandableCards/`),
 * as Web's Storybook `UI/ExpandableCards` shows it. The Paper shares the
 * card's `layoutId`, so Motion morphs the card into the modal and back; the
 * title shares one too, so it glides into place. The page behind keeps its
 * layout, so no card moves while one is edited.
 *
 * A click outside asks first when something changed (`onRequestClose`,
 * decision 16). Esc is not the dialog's: `useCardEditor` listens on the
 * document, so it still cancels after a date pick has left focus on the page.
 */
export type CardEditDialogProps = {
  /** The card's `layoutId` — its automation id. */
  layoutId: string;
  icon: string;
  title: string;
  /** The form, then the footer. */
  children: ReactNode;
  footer: ReactNode;
  onRequestClose: () => void;
};

const MotionPaper = motion.create(Paper);
const MotionBackdrop = motion.create(Backdrop);
// Cast as Web does: Motion's wrapper types drop MUI's `component` prop.
const MotionTypography = motion.create(Typography as ComponentType<TypographyProps>);

/**
 * Stands in for MUI's fade so Motion's morph is the only transition. The ref
 * must reach a real element or MUI's focus trap never takes focus — and Esc
 * then never reaches the dialog (Web's note). `onEnter` tells the Modal it has
 * entered, as Fade does.
 */
const NoTransition = forwardRef<HTMLDivElement, TransitionProps & { children: ReactNode }>(
  ({ children, in: inProp, onEnter, onEntered }, ref) => {
    useEffect(() => {
      if (inProp) {
        onEnter?.(document.body, false);
        onEntered?.(document.body, false);
      }
    }, [inProp, onEnter, onEntered]);
    if (!isValidElement<{ ref?: Ref<HTMLDivElement> }>(children)) return null;
    // The ref is handed on to the element, never read here — what MUI's Fade does.
    // eslint-disable-next-line react-hooks/refs
    return cloneElement(children, { ref });
  },
);
NoTransition.displayName = 'NoTransition';

export const CardEditDialog = ({
  layoutId,
  icon,
  title,
  children,
  footer,
  onRequestClose,
}: CardEditDialogProps) => {
  const headingId = `${layoutId}-dialog-title`;
  return (
    <Dialog
      open
      onClose={onRequestClose}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-labelledby={headingId}
      data-automation-id={`${layoutId}-dialog`}
      // PaperComponent, as Web does: as `slots.paper` it would replace MUI's
      // styled paper and lose the dialog's width, margins and centring.
      PaperComponent={MotionPaper as ComponentType<PaperProps>}
      slots={{ transition: NoTransition, backdrop: MotionBackdrop }}
      slotProps={{
        paper: {
          layoutId,
          sx: {
            borderRadius: 'var(--border-radius-x-large)',
            border: 'none',
            boxShadow: 'var(--shadow-drawer)',
          },
        } as PaperProps & MotionProps,
        backdrop: {
          initial: { opacity: 0 },
          animate: { opacity: 0.7 },
          exit: { opacity: 0 },
          transition: { ease: 'easeOut', duration: 0.15 },
          sx: { backgroundColor: 'var(--color-black-20)' },
        } as BackdropProps & MotionProps,
      }}
    >
      <DialogContent sx={{ pt: 3 }}>
        <Stack sx={{ gap: 2 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minHeight: 32 }}>
            <CardIcon name={icon} />
            <MotionTypography
              layoutId={`${layoutId}-title`}
              id={headingId}
              variant="bodyBold"
              component="h2"
            >
              {title}
            </MotionTypography>
          </Stack>
          <Stack sx={{ gap: 2 }} data-automation-id={`${layoutId}-form`}>
            {children}
            {footer}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
